import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

function AdminDriverForm({ user }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    // Compte
    email: '',
    password: '',

    // 1. Identification
    matricule_salarie: '',
    matricule_interne: '',
    civilite: 'M.',
    sexe: 'M',
    nom_naissance: '',
    nom_marital: '',
    nom_usuel: '',
    prenom: '',
    numero_securite_sociale: '',
    situation_familiale: 'celibataire',

    // 2. Naissance et nationalité
    date_naissance: '',
    departement_naissance: '',
    commune_naissance: '',
    code_pays_naissance: 'FR',
    pays_naissance: 'France',
    code_nationalite: 'FR',
    nationalite: 'Française',

    // 3. Coordonnées
    voie: '',
    complement_adresse: '',
    code_postal: '',
    ville: '',
    pays_residence: 'France',
    domicilie_fiscalement_france: true,

    // 4. Contacts
    telephone_portable: '',
    email_personnel: '',

    // 5. Entrée entreprise
    date_entree: '',
    heure_entree: '09:00',

    // 6. Niveau d'étude
    nature_diplome: '',
    niveau_diplome: '',
    nom_diplome: '',
    informations_complementaires: '',
    niveau_formation_eleve: '',
    niveau_diplome_prepare: '',

    // Informations véhicule
    vehicle_plate: '',
    vehicle_type: '',
    license_number: '',
    license_validity: '',

    // Clients
    client_ids: [],
    status: 'active'
  });

  useEffect(() => {
    fetchClients();
    if (isEdit) {
      fetchDriver();
    }
  }, [id]);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/clients', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Erreur clients:', error);
    }
  };

  const fetchDriver = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/drivers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const driver = response.data;
      setFormData({
        ...formData,
        email: driver.email || '',
        matricule_salarie: driver.matricule_salarie || '',
        matricule_interne: driver.internal_id || '',
        civilite: driver.civilite || 'M.',
        sexe: driver.sexe || 'M',
        nom_naissance: driver.lastname || '',
        prenom: driver.firstname || '',
        telephone_portable: driver.phone || '',
        voie: driver.address || '',
        date_entree: driver.hire_date || '',
        vehicle_plate: driver.vehicle_plate || '',
        vehicle_type: driver.vehicle_type || '',
        license_number: driver.license_number || '',
        license_validity: driver.license_validity || '',
        client_ids: driver.clients ? driver.clients.map(c => c.id) : [],
        status: driver.status || 'active',
        // Les autres champs restent vides s'ils n'existent pas
        numero_securite_sociale: driver.numero_securite_sociale || '',
        situation_familiale: driver.situation_familiale || 'celibataire',
        date_naissance: driver.date_naissance || '',
        code_postal: driver.code_postal || '',
        ville: driver.ville || ''
      });
    } catch (error) {
      console.error('Erreur chargement chauffeur:', error);
      alert('Erreur lors du chargement du chauffeur');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEdit && !formData.password) {
      alert('Le mot de passe est obligatoire pour créer un nouveau chauffeur');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const payload = {
        email: formData.email,
        password: formData.password,

        // Champs obligatoires
        firstname: formData.prenom,
        lastname: formData.nom_naissance,
        hire_date: formData.date_entree,

        // Identification
        matricule_salarie: formData.matricule_salarie,
        internal_id: formData.matricule_interne,
        civilite: formData.civilite,
        sexe: formData.sexe,
        nom_marital: formData.nom_marital,
        nom_usuel: formData.nom_usuel,
        numero_securite_sociale: formData.numero_securite_sociale,
        situation_familiale: formData.situation_familiale,

        // Naissance
        date_naissance: formData.date_naissance,
        departement_naissance: formData.departement_naissance,
        commune_naissance: formData.commune_naissance,
        code_pays_naissance: formData.code_pays_naissance,
        pays_naissance: formData.pays_naissance,
        code_nationalite: formData.code_nationalite,
        nationalite: formData.nationalite,

        // Coordonnées
        address: formData.voie,
        complement_adresse: formData.complement_adresse,
        code_postal: formData.code_postal,
        ville: formData.ville,
        pays_residence: formData.pays_residence,
        domicilie_fiscalement_france: formData.domicilie_fiscalement_france,

        // Contacts
        phone: formData.telephone_portable,
        email_personnel: formData.email_personnel,

        // Entrée
        heure_entree: formData.heure_entree,

        // Diplôme
        nature_diplome: formData.nature_diplome,
        niveau_diplome: formData.niveau_diplome,
        nom_diplome: formData.nom_diplome,
        informations_complementaires: formData.informations_complementaires,
        niveau_formation_eleve: formData.niveau_formation_eleve,
        niveau_diplome_prepare: formData.niveau_diplome_prepare,

        // Véhicule
        vehicle_plate: formData.vehicle_plate,
        vehicle_type: formData.vehicle_type,
        license_number: formData.license_number,
        license_validity: formData.license_validity,

        // Clients
        client_ids: formData.client_ids,
        status: formData.status,

        // Agency
        agency: formData.agency || null
      };

      if (isEdit) {
        await axios.put(`/api/drivers/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Chauffeur modifié avec succès!');
      } else {
        await axios.post('/api/drivers/register', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Chauffeur créé avec succès!');
      }

      navigate('/drivers');
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleClientToggle = (clientId) => {
    if (formData.client_ids.includes(clientId)) {
      setFormData({
        ...formData,
        client_ids: formData.client_ids.filter(id => id !== clientId)
      });
    } else {
      setFormData({
        ...formData,
        client_ids: [...formData.client_ids, clientId]
      });
    }
  };

  if (loading && isEdit) {
    return <div className="container"><div className="loading">Chargement...</div></div>;
  }

  return (
    <div className="container">
      <div className="card">
        <h2>{isEdit ? 'Modifier le chauffeur' : 'Créer un nouveau chauffeur'}</h2>

        <form onSubmit={handleSubmit}>
          {/* COMPTE */}
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Compte</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Mot de passe {!isEdit && '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  required={!isEdit}
                  placeholder={isEdit ? 'Laisser vide pour ne pas changer' : ''}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
            </div>
          </div>

          {/* 1. IDENTIFICATION */}
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#e8f4f8', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>1. Identification du salarié</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Matricule salarié</label>
                <input
                  type="text"
                  value={formData.matricule_salarie}
                  onChange={(e) => handleChange('matricule_salarie', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Matricule interne</label>
                <input
                  type="text"
                  value={formData.matricule_interne}
                  onChange={(e) => handleChange('matricule_interne', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Civilité</label>
                <select
                  value={formData.civilite}
                  onChange={(e) => handleChange('civilite', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                >
                  <option value="M.">M.</option>
                  <option value="Mme">Mme</option>
                  <option value="Mlle">Mlle</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Sexe</label>
                <select
                  value={formData.sexe}
                  onChange={(e) => handleChange('sexe', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                >
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Nom de naissance *</label>
                <input
                  type="text"
                  value={formData.nom_naissance}
                  onChange={(e) => handleChange('nom_naissance', e.target.value)}
                  required
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Nom marital</label>
                <input
                  type="text"
                  value={formData.nom_marital}
                  onChange={(e) => handleChange('nom_marital', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Nom usuel</label>
                <input
                  type="text"
                  value={formData.nom_usuel}
                  onChange={(e) => handleChange('nom_usuel', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Prénom *</label>
                <input
                  type="text"
                  value={formData.prenom}
                  onChange={(e) => handleChange('prenom', e.target.value)}
                  required
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Numéro de Sécurité Sociale (NIR)</label>
                <input
                  type="text"
                  value={formData.numero_securite_sociale}
                  onChange={(e) => handleChange('numero_securite_sociale', e.target.value)}
                  maxLength="15"
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Situation familiale</label>
                <select
                  value={formData.situation_familiale}
                  onChange={(e) => handleChange('situation_familiale', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                >
                  <option value="celibataire">Célibataire</option>
                  <option value="marie">Marié(e)</option>
                  <option value="pacse">Pacsé(e)</option>
                  <option value="divorce">Divorcé(e)</option>
                  <option value="veuf">Veuf/Veuve</option>
                </select>
              </div>
            </div>
          </div>

          {/* 2. NAISSANCE ET NATIONALITÉ */}
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#fff3cd', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>2. Naissance et nationalité</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Date de naissance</label>
                <input
                  type="date"
                  value={formData.date_naissance}
                  onChange={(e) => handleChange('date_naissance', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Département de naissance</label>
                <input
                  type="text"
                  value={formData.departement_naissance}
                  onChange={(e) => handleChange('departement_naissance', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Commune de naissance</label>
                <input
                  type="text"
                  value={formData.commune_naissance}
                  onChange={(e) => handleChange('commune_naissance', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Pays de naissance</label>
                <input
                  type="text"
                  value={formData.pays_naissance}
                  onChange={(e) => handleChange('pays_naissance', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Code pays naissance</label>
                <input
                  type="text"
                  value={formData.code_pays_naissance}
                  onChange={(e) => handleChange('code_pays_naissance', e.target.value)}
                  maxLength="2"
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Nationalité</label>
                <input
                  type="text"
                  value={formData.nationalite}
                  onChange={(e) => handleChange('nationalite', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
            </div>
          </div>

          {/* 3. COORDONNÉES */}
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#d1ecf1', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>3. Coordonnées</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Voie (Adresse)</label>
                <input
                  type="text"
                  value={formData.voie}
                  onChange={(e) => handleChange('voie', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Complément d'adresse</label>
                <input
                  type="text"
                  value={formData.complement_adresse}
                  onChange={(e) => handleChange('complement_adresse', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Code postal</label>
                <input
                  type="text"
                  value={formData.code_postal}
                  onChange={(e) => handleChange('code_postal', e.target.value)}
                  maxLength="5"
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Ville</label>
                <input
                  type="text"
                  value={formData.ville}
                  onChange={(e) => handleChange('ville', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Pays de résidence</label>
                <input
                  type="text"
                  value={formData.pays_residence}
                  onChange={(e) => handleChange('pays_residence', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.domicilie_fiscalement_france}
                    onChange={(e) => handleChange('domicilie_fiscalement_france', e.target.checked)}
                  />
                  Domicilié fiscalement en France
                </label>
              </div>
            </div>
          </div>

          {/* 4. CONTACTS */}
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#d4edda', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>4. Contacts</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Téléphone portable personnel</label>
                <input
                  type="tel"
                  value={formData.telephone_portable}
                  onChange={(e) => handleChange('telephone_portable', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Adresse mail personnelle</label>
                <input
                  type="email"
                  value={formData.email_personnel}
                  onChange={(e) => handleChange('email_personnel', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
            </div>
          </div>

          {/* 5. ENTRÉE ENTREPRISE */}
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8d7da', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>5. Entrée dans l'entreprise</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Date d'entrée *</label>
                <input
                  type="date"
                  value={formData.date_entree}
                  onChange={(e) => handleChange('date_entree', e.target.value)}
                  required
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Heure d'entrée (hh:mm)</label>
                <input
                  type="time"
                  value={formData.heure_entree}
                  onChange={(e) => handleChange('heure_entree', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
            </div>
          </div>

          {/* 6. NIVEAU D'ÉTUDE */}
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#e2e3e5', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>6. Niveau d'étude</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Nature du diplôme</label>
                <input
                  type="text"
                  value={formData.nature_diplome}
                  onChange={(e) => handleChange('nature_diplome', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Niveau</label>
                <input
                  type="text"
                  value={formData.niveau_diplome}
                  onChange={(e) => handleChange('niveau_diplome', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Nom du diplôme</label>
                <input
                  type="text"
                  value={formData.nom_diplome}
                  onChange={(e) => handleChange('nom_diplome', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Niveau de formation le plus élevé</label>
                <input
                  type="text"
                  value={formData.niveau_formation_eleve}
                  onChange={(e) => handleChange('niveau_formation_eleve', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Informations complémentaires</label>
                <textarea
                  value={formData.informations_complementaires}
                  onChange={(e) => handleChange('informations_complementaires', e.target.value)}
                  rows="3"
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd', fontFamily: 'inherit' }}
                />
              </div>
            </div>
          </div>

          {/* VÉHICULE */}
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#cfe2ff', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Informations véhicule</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Plaque d'immatriculation</label>
                <input
                  type="text"
                  value={formData.vehicle_plate}
                  onChange={(e) => handleChange('vehicle_plate', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Type de véhicule</label>
                <input
                  type="text"
                  value={formData.vehicle_type}
                  onChange={(e) => handleChange('vehicle_type', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Numéro de permis</label>
                <input
                  type="text"
                  value={formData.license_number}
                  onChange={(e) => handleChange('license_number', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Validité du permis</label>
                <input
                  type="date"
                  value={formData.license_validity}
                  onChange={(e) => handleChange('license_validity', e.target.value)}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
            </div>
          </div>

          {/* CLIENTS */}
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#fff3cd', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Clients assignés</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              {clients.map(client => (
                <label key={client.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.client_ids.includes(client.id)}
                    onChange={() => handleClientToggle(client.id)}
                  />
                  {client.name}
                </label>
              ))}
            </div>
          </div>

          {/* STATUT */}
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Statut</h3>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Statut</label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
              >
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>
          </div>

          {/* BOUTONS */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => navigate('/drivers')}
              style={{
                padding: '0.8rem 1.5rem',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '0.8rem 1.5rem',
                background: loading ? '#ccc' : '#2F7D5B',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {loading ? 'Enregistrement...' : (isEdit ? 'Modifier' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminDriverForm;
